import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import ContactUsImage from "../../image/contact-us.png";
import { getTranslation } from 'common';
import { useTranslations, useUser } from 'hooks';
import { baseApi } from 'api';

interface BillingContactDialogProps {
  open: boolean;
  onClose: () => void;
}

const BillingContactDialog: React.FC<BillingContactDialogProps> = ({ open, onClose }) => {
  const user = useUser()
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const translations = useTranslations()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitted(false);

    try {
      setSending(true);
      const payload = {
        to_name: "",
        to_email: "support@flowenti.com",
        subject,
        message: `${message}\n\nEmail utente: ${user?.email || email}`,
        email_type: 'support'
      };
      const response = await baseApi.post('/mail/support', payload);

      if (response.status === 200 || response.status === 202) {
        setSubmitted(true);
        setMessage('');
        setSubject('');
        if (!user?.email) setEmail('');
      } else {
        setError(getTranslation(translations, "send_support.error"));
      }
    } catch (err) {
      setError(getTranslation(translations, "support_support.server.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" sx={{ zIndex: 999999999 }}>
      <Box display="flex" flexDirection="row" width="100%">
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Box
            component="img"
            src={ContactUsImage}
            alt="Contattaci"
            sx={{ maxHeight: '50vh', width: '100%', objectFit: 'contain' }}
          />
        </Box>
        <Box flex={1} display="flex" flexDirection="column" p={2}>
          <DialogTitle>{getTranslation(translations, "support.title")}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              disabled={!!user?.email}
            />
            <TextField
              label={getTranslation(
                translations,
                "generic_email.subject"
              )}
              fullWidth
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              margin="normal"
            />
            <TextField
              margin="dense"
              label={getTranslation(translations, "generic_email.message")}
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              size="small"
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={onClose} color="secondary">
              {getTranslation(translations, "generic.button.cancel")}
            </Button>
            <Button onClick={handleSubmit} color="primary" disabled={sending || !message || !subject} variant="contained">
              {getTranslation(translations, "generic.send.caption")}
            </Button>
          </DialogActions>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb:2 }}>
          {error}
        </Alert>
      )}

      {submitted && (
        <Alert severity="success" sx={{ mt: 2, mb:2 }}>
          {getTranslation(translations, "support_message.sent")}
        </Alert>
      )}
    </Dialog>

  );
};

export default BillingContactDialog;